import React, { useMemo } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  LocalGasStation,
  DirectionsCar,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Speed,
  LocationOn,
  Info,
} from "@mui/icons-material";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import NavigationHeader from "../../components/NavigationHeader";
import FuelReportsFilters from "../../components/fuel-reports/FuelReportsFilters";
import {
  FuelReportsProvider,
  useFuelReports,
} from "../../contexts/FuelReportsContext";
import { FuelReportsService } from "../../services/fuelReportsService";
import { formatNumber, formatCurrency } from "../../utils/formatters";
import { commonTypographyProps } from "../../utils/styles";

/** Dashboard API only uses date fields; keep query key narrow so other filters do not refetch or invalidate this query. */
function useDashboardDateQueryKey(filters: {
  dateFilter?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useMemo(
    () =>
      [
        "fuel-dashboard-analytics",
        filters.dateFilter ?? "",
        filters.dateFilter === "custom" ? (filters.startDate ?? "") : "",
        filters.dateFilter === "custom" ? (filters.endDate ?? "") : "",
      ] as const,
    [filters.dateFilter, filters.startDate, filters.endDate]
  );
}

const FuelDashboardContent: React.FC = () => {
  const { filters, isValidDateRange } = useFuelReports();
  const dashboardDateQueryKey = useDashboardDateQueryKey(filters);

  const dashboardQueryEnabled =
    Boolean(filters.dateFilter) &&
    (filters.dateFilter !== "custom" ||
      (Boolean(filters.startDate && filters.endDate) &&
        isValidDateRange(filters.startDate, filters.endDate)));

  const {
    data: dashboardData,
    isPending,
    isError,
    isFetching,
    error,
  } = useQuery({
    queryKey: dashboardDateQueryKey,
    queryFn: () =>
      FuelReportsService.getDashboardAnalytics({
        dateFilter: filters.dateFilter,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    enabled: dashboardQueryEnabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading fuel dashboard: {error?.message}
      </Alert>
    );
  }

  if (isPending && !dashboardData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const overview = dashboardData?.data?.overview;
  const vehicleInsights = dashboardData?.data?.vehicle_insights;
  const regionalInsights = dashboardData?.data?.regional_insights;
  const monthOnMonthTrends = dashboardData?.data?.month_on_month_trends;

  const renderOverviewCards = () => {
    if (!overview) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1,
              }}
            >
              <Avatar sx={{ bgcolor: "#2196F3", width: 32, height: 32 }}>
                <DirectionsCar sx={{ fontSize: "0.875rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "0.8rem" }}
                >
                  {formatNumber(overview.total_vehicles)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Total Vehicles
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1,
              }}
            >
              <Avatar sx={{ bgcolor: "#FF9800", width: 32, height: 32 }}>
                <LocalGasStation sx={{ fontSize: "0.875rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "0.8rem" }}
                >
                  {formatNumber(overview.total_fuel_consumed, 1)}L
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Total Fuel
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1,
              }}
            >
              <Avatar sx={{ bgcolor: "#4CAF50", width: 32, height: 32 }}>
                <AttachMoney sx={{ fontSize: "0.875rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "0.8rem" }}
                >
                  {formatCurrency(overview.total_fuel_cost)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Total Cost
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1,
              }}
            >
              <Avatar sx={{ bgcolor: "#9C27B0", width: 32, height: 32 }}>
                <Speed sx={{ fontSize: "0.875rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "0.8rem" }}
                >
                  {formatNumber(overview.avg_efficiency, 1)} km/l
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Avg Efficiency
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderEfficiencyDistribution = () => {
    if (!overview?.efficiency_distribution) return null;

    const distribution = overview.efficiency_distribution;
    const total = Object.values(distribution).reduce(
      (sum: number, count: any) => sum + (count as number),
      0
    );

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1, pb: 1 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              display: "flex",
              alignItems: "center",
              fontSize: "0.75rem",
            }}
          >
            <TrendingUp sx={{ mr: 0.5, fontSize: "0.875rem" }} />
            Fleet Efficiency Distribution
          </Typography>
          <Grid container spacing={1}>
            {Object.entries(distribution).map(([category, count]) => {
              const percentage =
                total > 0
                  ? (((count as number) / total) * 100).toFixed(1)
                  : "0";
              return (
                <Grid item xs={6} sm={2.4} key={category}>
                  <Box sx={{ textAlign: "center", py: 0.5 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", fontSize: "0.8rem", mb: 0.25 }}
                    >
                      {count as number}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        textTransform: "capitalize",
                        fontSize: "0.75rem",
                        mb: 0.25,
                      }}
                    >
                      {category}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      ({percentage}%)
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderTopPerformers = () => {
    if (!vehicleInsights) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.8rem",
                }}
              >
                <TrendingDown
                  sx={{ mr: 1, color: "error.main", fontSize: "1rem" }}
                />
                Top Fuel Consumers
              </Typography>
              {vehicleInsights.top_consumers
                ?.slice(0, 3)
                .map((vehicle, index) => (
                  <Box key={vehicle.vehicleNumber} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {vehicle.vehicleNumber}
                      </Typography>
                      <Chip
                        label={`${formatNumber(
                          vehicle.total_fuel_consumed_liters,
                          1
                        )}L`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {vehicle.model} •{" "}
                      {formatCurrency(vehicle.total_fuel_cost_inr)}
                    </Typography>
                    {index < 2 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.8rem",
                }}
              >
                <Speed
                  sx={{ mr: 1, color: "success.main", fontSize: "1rem" }}
                />
                Efficiency Leaders
              </Typography>
              {vehicleInsights.efficiency_leaders
                ?.slice(0, 3)
                .map((vehicle, index) => (
                  <Box key={vehicle.vehicleNumber} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {vehicle.vehicleNumber}
                      </Typography>
                      <Chip
                        label={`${formatNumber(
                          vehicle.fuel_efficiency_kmpl,
                          1
                        )} km/l`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {vehicle.model} • {vehicle.efficiency_rating}
                    </Typography>
                    {index < 2 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.8rem",
                }}
              >
                <AttachMoney
                  sx={{ mr: 1, color: "primary.main", fontSize: "1rem" }}
                />
                Cost Efficient
              </Typography>
              {vehicleInsights.cost_efficiency
                ?.slice(0, 3)
                .map((vehicle, index) => (
                  <Box key={vehicle.vehicleNumber} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {vehicle.vehicleNumber}
                      </Typography>
                      <Chip
                        label={`₹${formatNumber(vehicle.cost_per_km, 2)}/km`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {vehicle.model} •{" "}
                      {formatNumber(vehicle.fuel_efficiency_kmpl, 1)} km/l
                    </Typography>
                    {index < 2 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderMonthOnMonthTrends = () => {
    if (!monthOnMonthTrends) return null;

    const { summary } = monthOnMonthTrends;
    const trendColor =
      summary.trend_direction === "Increasing" ? "error.main" : "success.main";
    const TrendIcon =
      summary.trend_direction === "Increasing" ? TrendingUp : TrendingDown;

    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center" }}
          >
            <TrendIcon sx={{ mr: 1, color: trendColor }} />
            Fuel Consumption Trends ({summary.months_analyzed} months)
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: trendColor }}
                >
                  {formatNumber(summary.avg_monthly_fuel, 1)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Monthly Fuel
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {formatNumber(summary.total_fuel_consumed, 1)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Period Fuel
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Chip
                  label={summary.trend_direction}
                  color={
                    summary.trend_direction === "Increasing"
                      ? "error"
                      : "success"
                  }
                  icon={<TrendIcon />}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Trend Direction
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {overview?.data_sources || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Sources
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -6,
            left: 0,
            right: 0,
            height: 2,
            borderRadius: 1,
            zIndex: 1,
          }}
        />
      )}
      <Box>
        {renderOverviewCards()}
        {renderEfficiencyDistribution()}
        {renderTopPerformers()}
        {renderMonthOnMonthTrends()}

      {/* Additional Insights */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <LocationOn sx={{ mr: 1 }} />
                Regional Insights
              </Typography>
              {regionalInsights?.top_consuming_regions
                ?.slice(0, 3)
                .map((region, index) => (
                  <Box key={`${region.region}-${index}`} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {region.district}
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {formatNumber(region.total_fuel_consumed_liters, 1)}L
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {region.region} • {region.total_vehicles} vehicles
                    </Typography>
                    {index < 2 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <Info sx={{ mr: 1 }} />
                Quick Stats
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(
                        overview?.total_fuel_cost
                          ? overview.total_fuel_cost / overview.total_vehicles
                          : 0
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Cost/Vehicle
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {formatNumber(
                        overview?.total_fuel_consumed
                          ? overview.total_fuel_consumed /
                              overview.total_vehicles
                          : 0,
                        1
                      )}
                      L
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Fuel/Vehicle
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

const FuelDashboard: React.FC = () => {
  return (
    <FuelReportsProvider>
      <Container maxWidth={false}>
        <Box sx={{ py: 2 }}>
          <NavigationHeader
            title="Fuel Analytics Dashboard"
            backNavigationEnabled={true}
          />

          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ ...commonTypographyProps }}>
                Comprehensive fuel analytics dashboard showing fleet
                performance, efficiency trends, and regional insights. Use
                filters to analyze specific time periods or vehicle segments.
              </Typography>
            </Box>
          </Alert>

          <FuelReportsFilters onApplyFilters={() => {}} />

          <FuelDashboardContent />
        </Box>
      </Container>
    </FuelReportsProvider>
  );
};

export default FuelDashboard;
